/** Journey Detail screen — shows one journey with a map and colored legs. */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Map, { Marker, NavigationControl, Popup, Source, Layer, type MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Journey, JourneyLeg } from '@/services/trip-planner';
import { DARK_MAP_STYLE } from '@/services/map-style';

const MODE_COLORS: Record<string, string> = {
  Train: '#F6891F', Bus: '#0072C6', Ferry: '#61B255', Metro: '#0099CD', 'Light Rail': '#D81E34', Walk: '#9E9E9E',
};
const MODE_ICONS: Record<string, string> = {
  Train: '🚆', Bus: '🚌', Ferry: '⛴', Metro: '🚇', 'Light Rail': '🚋', Walk: '🚶',
};
const BADGE_LETTER: Record<string, string> = {
  Train: 'T', Bus: 'B', Ferry: 'F', Metro: 'M', 'Light Rail': 'L',
};

const LEG_PALETTE = ['#FF6B00', '#0072C6', '#61B255', '#D81E34', '#9C27B0', '#0099CD'];

function legColor(mode: string, index: number): string {
  return MODE_COLORS[mode] || LEG_PALETTE[index % LEG_PALETTE.length];
}

function fmtTime(iso: string) { if (!iso) return '—'; return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }); }
function fmtDur(s: number) { if (!s || isNaN(s) || s < 0) return '—'; const h = Math.floor(s/3600); const m = Math.floor((s%3600)/60); return h>0?`${h}h${m}m`:m>0?`${m}min`:'<1min'; }

function shortName(name: string, modeName: string): string {
  if (!name) return '';
  const withoutPlatform = name.replace(/,\s*(Platform|Stand)\s+[^,]+(?=,|$).*/i, '');
  // Strip trailing ", Suburb" for all modes (not just Train)
  const idx = withoutPlatform.lastIndexOf(',');
  if (idx > 0) return withoutPlatform.slice(0, idx).trim();
  return withoutPlatform;
}

function interpolateTime(t1: string, t2: string): string {
  if (!t1 || !t2) return '';
  const d1 = new Date(t1).getTime(), d2 = new Date(t2).getTime();
  if (isNaN(d1) || isNaN(d2)) return '';
  return new Date((d1 + d2) / 2).toISOString();
}

export function JourneyDetailScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { journeys: Journey[]; index: number; origin: string; dest: string } | null;
  const [selectedLeg, setSelectedLeg] = useState(-1);
  const mapRef = useRef<MapRef>(null);
  const [mapReady, setMapReady] = useState(false);
  const [selectedMapStop, setSelectedMapStop] = useState<{ name: string; coords: [number, number]; mode: string; color: string } | null>(null);

  if (!state || !state.journeys?.length) {
    return (
      <main className="page-container">
        <button className="back-button" onClick={() => navigate(-1)}>← Back</button>
        <div className="placeholder-screen"><span className="icon">🧭</span><p className="label">No journey data</p></div>
      </main>
    );
  }

  const journey = state.journeys[state.index];
  if (!journey) return <main className="page-container"><p>Journey not found</p></main>;

  const originName = (state.origin && !/^\d+$/.test(state.origin)) ? state.origin : shortName(journey.legs[0]?.origin?.name || state.origin, journey.legs[0]?.modeName);
  const destName = (state.dest && !/^\d+$/.test(state.dest)) ? state.dest : shortName(journey.legs[journey.legs.length - 1]?.destination?.name || state.dest, journey.legs[journey.legs.length - 1]?.modeName);

  const sel = selectedLeg >= 0 ? journey.legs[selectedLeg] : null;

  // Build per-leg GeoJSON line features — use actual path if available, else straight lines between stops
  const { legLines, allCoords } = useMemo(() => {
    const lines: { legIdx: number; mode: string; coords: [number, number][]; isWalk: boolean; color: string }[] = [];
    const coords: [number, number][] = [];
    for (let i = 0; i < journey.legs.length; i++) {
      const leg = journey.legs[i];
      // Prefer actual path polyline (follows roads/tracks), fallback to stop-to-stop straight lines
      const pts: [number, number][] = (leg.path && leg.path.length >= 2)
        ? leg.path.filter(point => Number.isFinite(point[0]) && Number.isFinite(point[1]))
        : (() => {
            const p: [number, number][] = [];
            if (leg.origin.coords) p.push(leg.origin.coords);
            if (leg.stops) for (const s of leg.stops) if (s.coords) p.push(s.coords);
            if (leg.destination.coords) p.push(leg.destination.coords);
            return p;
          })();
      if (pts.length >= 2) {
        lines.push({ legIdx: i, mode: leg.modeName, coords: pts, isWalk: leg.modeName === 'Walk', color: legColor(leg.modeName, i) });
        coords.push(...pts);
      }
    }
    return { legLines: lines, allCoords: coords };
  }, [journey]);

  const visibleMapStops = useMemo(() => {
    const stops: { name: string; coords: [number, number]; mode: string; color: string }[] = [];
    const seen = new Set<string>();
    journey.legs.forEach((leg, legIdx) => {
      if (selectedLeg >= 0 && legIdx !== selectedLeg) return;
      const candidates = [leg.origin, ...(leg.stops || []), leg.destination];
      candidates.forEach(stop => {
        if (!stop.coords) return;
        const key = `${stop.coords[0].toFixed(6)}:${stop.coords[1].toFixed(6)}:${stop.name}`;
        if (seen.has(key)) return;
        seen.add(key);
        stops.push({
          name: shortName(stop.name, leg.modeName),
          coords: stop.coords,
          mode: leg.modeName,
          color: legColor(leg.modeName, legIdx),
        });
      });
    });
    return stops;
  }, [journey, selectedLeg]);

  const centerLat = allCoords.length > 0 ? allCoords.reduce((s, c) => s + c[1], 0) / allCoords.length : -33.8688;
  const centerLon = allCoords.length > 0 ? allCoords.reduce((s, c) => s + c[0], 0) / allCoords.length : 151.2093;

  useEffect(() => {
    const coords = selectedLeg >= 0 ? legLines.find(line => line.legIdx === selectedLeg)?.coords || [] : allCoords;
    if (!mapReady || !coords.length || !mapRef.current) return;
    const lons = coords.map(coord => coord[0]); const lats = coords.map(coord => coord[1]);
    const west = Math.min(...lons); const east = Math.max(...lons);
    const south = Math.min(...lats); const north = Math.max(...lats);
    if (west === east && south === north) mapRef.current.flyTo({ center: [west, south], zoom: 16, duration: 600 });
    else mapRef.current.fitBounds([[west, south], [east, north]], { padding: 45, duration: 650, maxZoom: 16 });
  }, [selectedLeg, legLines, allCoords, mapReady]);

  return (
    <main className="page-container">
      <button className="back-button" onClick={() => navigate(-1)}>← Back to Plan</button>
      <h1 className="page-title">{originName} → {destName}</h1>
      <p className="stop-id">{fmtTime(journey.departure)} – {fmtTime(journey.arrival)} • {fmtDur(journey.duration)} • {journey.transfers} transfer{journey.transfers !== 1 ? 's' : ''}</p>

      <div className="map-container" style={{ height: 250, marginBottom: 16, borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
        <Map ref={mapRef} onLoad={() => setMapReady(true)} initialViewState={{ latitude: centerLat, longitude: centerLon, zoom: allCoords.length > 0 ? 13 : 12 }} mapStyle={DARK_MAP_STYLE} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} attributionControl={false}>
          <NavigationControl position="top-right" />

          {/* Per-leg path lines */}
          {legLines.map((ll) => (
            <Source key={`leg-line-${ll.legIdx}`} id={`leg-line-${ll.legIdx}`} type="geojson" data={{
              type: 'FeatureCollection' as const,
              features: [{ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: ll.coords } }],
            }}>
              <Layer
                id={`leg-line-layer-${ll.legIdx}`}
                type="line"
                paint={{
                  'line-color': ll.color,
                  'line-width': selectedLeg === -1 ? 5 : (ll.legIdx === selectedLeg ? 7 : 3),
                  'line-opacity': selectedLeg === -1 ? 0.95 : (ll.legIdx === selectedLeg ? 1 : 0.38),
                  ...(ll.isWalk ? { 'line-dasharray': [4, 4] } : {}),
                }}
              />
            </Source>
          ))}

          {/* All itinerary stops are visible initially; selecting a leg filters them to that step. */}
          {visibleMapStops.map((stop, stopIdx) => (
            <Marker key={`${stop.coords[0]}-${stop.coords[1]}-${stopIdx}`} latitude={stop.coords[1]} longitude={stop.coords[0]}>
              <button
                type="button"
                className="stop-marker-badge"
                style={{ background: stop.color, transform: selectedLeg >= 0 ? 'scale(1.15)' : undefined, cursor: 'pointer' }}
                aria-label={`Show ${stop.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedMapStop(stop);
                }}
              >
                {BADGE_LETTER[stop.mode] || 'S'}
              </button>
            </Marker>
          ))}
          {selectedMapStop && (
            <Popup
              longitude={selectedMapStop.coords[0]}
              latitude={selectedMapStop.coords[1]}
              anchor="bottom"
              offset={14}
              closeOnClick={false}
              onClose={() => setSelectedMapStop(null)}
            >
              <div className="vehicle-popup">
                <strong>{selectedMapStop.name}</strong>
                <p>{selectedMapStop.mode} stop</p>
              </div>
            </Popup>
          )}
        </Map>
      </div>

      {/* Leg boxes */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {journey.legs.map((leg, i) => (
          <button key={i} className={`leg-box${i === selectedLeg ? ' leg-box--selected' : ''}`}
            style={{ borderLeft: `4px solid ${legColor(leg.modeName, i)}` }}
            onClick={() => setSelectedLeg(i === selectedLeg ? -1 : i)}>
            <LegSummary leg={leg} step={i + 1} />
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
        {selectedLeg >= 0 && sel ? (
          <div className="card"><LegStops leg={sel} /></div>
        ) : (
          journey.legs.map((leg, index) => (
            <div className="card" key={`leg-stops-${index}`} style={{ borderLeft: `4px solid ${legColor(leg.modeName, index)}` }}>
              <LegStops leg={leg} />
            </div>
          ))
        )}
      </div>
    </main>
  );
}

function LegSummary({ leg, step }: { leg: JourneyLeg; step: number }) {
  const color = MODE_COLORS[leg.modeName] || '#9E9E9E';
  const icon = MODE_ICONS[leg.modeName] || '🚌';
  let desc = '';
  if (leg.modeName === 'Walk') {
    desc = `Walk to ${shortName(leg.destination.name, 'Walk')}`;
  } else {
    const from = shortName(leg.origin.name, leg.modeName);
    const to = shortName(leg.destination.name, leg.modeName);
    const plat = leg.platform ? `, Platform ${leg.platform}` : '';
    desc = from !== to ? `${leg.modeName} from ${from}${plat} to ${to}` : `${leg.modeName} from ${from}`;
  }
  return (
    <div className="leg-box-inner">
      <div className="leg-box-icon" style={{ background: color }}>{icon}</div>
      <div className="leg-box-info">
        <div className="leg-box-desc">Step {step} — {desc}</div>
        <div className="leg-box-sub">{fmtTime(leg.origin.departure)} → {fmtTime(leg.destination.arrival)} • {fmtDur(leg.duration)}</div>
      </div>
    </div>
  );
}

function LegStops({ leg }: { leg: JourneyLeg }) {
  if (!leg) return <div className="placeholder-screen"><p className="label">Select a step above</p></div>;
  const color = MODE_COLORS[leg.modeName] || '#9E9E9E';
  const enriched = useMemo(() => {
    const stops = leg.stops && leg.stops.length > 0 ? leg.stops : [];
    if (stops.length === 0) {
      return [
        { name: shortName(leg.origin.name, leg.modeName), departure: leg.origin.departure, interp: false },
        { name: shortName(leg.destination.name, leg.modeName), departure: leg.destination.arrival, interp: false },
      ];
    }
    return stops.map((s, i) => {
      let dep = s.departure; let interpVal = false;
      if (!dep) {
        let prev = ''; for (let j = i - 1; j >= 0; j--) { prev = stops[j]?.departure || stops[j]?.arrival || ''; if (prev) break; }
        if (!prev) prev = leg.origin.departure;
        let next = ''; for (let j = i + 1; j < stops.length; j++) { next = stops[j]?.departure || stops[j]?.arrival || ''; if (next) break; }
        if (!next) next = leg.destination.arrival;
        const interp = interpolateTime(prev, next);
        if (interp) { dep = interp; interpVal = true; }
      }
      return { name: shortName(s.name, leg.modeName), departure: dep || '', interp: interpVal };
    });
  }, [leg]);

  return (
    <div>
      <h3 style={{ color, marginBottom: 12, fontSize: 15 }}>📍 {leg.modeName} — Stop by stop</h3>
      {enriched.map((s, i) => (
        <div key={i} className="leg-stop-row">
          <span className={s.interp ? 'leg-stop-name leg-stop-interpolated' : 'leg-stop-name'}>{s.name}</span>
          <span className={s.interp ? 'leg-stop-time leg-stop-interpolated' : 'leg-stop-time'}>{s.departure ? fmtTime(s.departure) : '—'}</span>
        </div>
      ))}
      {leg.isRealtime && <span className="leg-live">● Live tracking</span>}
    </div>
  );
}
